import { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useQuery } from "react-query";
import { useFormik } from "formik";
import * as yup from "yup";
import Select from "react-select";
import { format } from "date-fns";
import { isEmpty, cloneDeep } from "lodash";
import { toast } from "react-toastify";

import { useAuth } from "../../../hooks/useAuth";
import RequisitionService from "../../../hooks/requisitionService";
import ConfirmDialog from "../../ConfirmDialogue";
import eventBus from "../../../utils/EventBus";
import DamageBatchPicker from "./DamageBatchPicker";
import DamageRequestTable from "./DamageRequestTable";

export default function NewDamageRequestModal({
  setShowNewRequisitionModal,
  previousFormValues = {},
  handleCreatedRequisition,
  mainRefetch,
}) {
  const { backendUrl } = useAuth();
  const { createRequisitionMutation } = RequisitionService();

  const [tableData, setTableData] = useState([]);
  const [showBatchPicker, setShowBatchPicker] = useState(false);

  const getDepartments = async () => {
    let response = await fetch(`${backendUrl}/api/users/departments`, {
      method: "GET",
      headers: {
        Accept: "Application/json",
        "Content-Type": "Application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    data.departments = [
      { label: "Select Recipient's Department", value: "" },
      ...data.departments.map(el => ({
        ...el,
        label: el.Department,
        value: el.Department,
      })),
    ];
    return data;
  };

  const { data: { departments } = { departments: [] } } = useQuery(
    ["GET_DEPARTMENTS"],
    () => getDepartments(),
    {}
  );

  const getUsersInDepartments = async department => {
    let response = await fetch(
      `${backendUrl}/api/users/by-department/${department}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }

    const { data } = await response.json();
    data.users = [
      { label: "Select Recipient", value: "" },
      ...data.users.map(el => ({
        ...el,
        label: el.Name,
        value: el.Staff_ID,
      })),
    ];
    return data;
  };

  const formik = useFormik({
    initialValues: {
      title: previousFormValues.title || "",
      department: previousFormValues.department || "",
      recipient: previousFormValues.recipient || "",
      comment: previousFormValues.comment || "",
    },
    validationSchema: yup.object().shape({
      title: yup.string().required(),
      department: yup.string().required(),
      recipient: yup.string().required(),
    }),
    onSubmit: async values => {
      if (isEmpty(tableData)) {
        return toast.error("Please add at least one item from a batch");
      }

      if (
        !(await ConfirmDialog({
          title: "Send for Approval",
          description: "Are you sure you want to send this Damage Request?",
        }))
      ) {
        return;
      }

      const payload = cloneDeep(values);
      payload.type = "Damage Request";
      payload.requestto = values.recipient;
      payload.regdate = format(new Date(), "yyyy-MM-dd");
      payload.status = "Pending";
      payload.amount = 0;
      payload.description = tableData
        .map(
          el =>
            `${el.itemName} (Batch: ${el.batchBarCode}) - Qty: ${el.quantity}`
        )
        .join("\n");
      payload.tableData = JSON.stringify(tableData);
      delete payload.recipient;

      createRequisitionMutation.mutate(payload, {
        onSuccess: data => {
          formik.resetForm();
          setTableData([]);
          if (data.requisition) {
            eventBus.dispatch("REQUISITION_CREATED", data.requisition);
            if (handleCreatedRequisition)
              handleCreatedRequisition(data.requisition);
          }
          mainRefetch && mainRefetch();
          setShowNewRequisitionModal(false);
        },
        onError: ({ errors }) => {
          if (errors) formik.setErrors(errors);
        },
      });
    },
  });

  const {
    data: { users } = { users: [] },
    refetch: getUsers,
    isFetching: isLoadingUsers,
  } = useQuery(
    ["GET_USERS_IN_DEPARTMENTS", formik.values.department],
    () => getUsersInDepartments(formik.values.department),
    { enabled: false }
  );

  useEffect(() => {
    if (formik.values.department) getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.department]);

  const handleBatchesAdded = rows => {
    setTableData(prev => {
      let updated = [...prev];
      for (const row of rows) {
        const existingIndex = updated.findIndex(
          el => el.batchBarCode === row.batchBarCode
        );
        if (existingIndex !== -1) {
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: Number(updated[existingIndex].quantity) + row.quantity,
          };
        } else {
          updated = [...updated, row];
        }
      }
      return updated;
    });
  };

  const removeRow = index => {
    setTableData(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Modal
        show={true}
        onHide={() => setShowNewRequisitionModal(false)}
        dialogClassName="requisition-details-modal preview-modal"
        backdropClassName="global-backdrop"
        centered
        animation={false}
        enforceFocus={false}
        fullscreen
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <h1 className="py-2">New Damage Request</h1>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="preview p-0">
          <div className="preview-content p-0">
            <div className="content">
              <div className="preview-body">
                <Form
                  className="mb-3"
                  id="damage-requisition-form"
                  noValidate
                  onSubmit={formik.handleSubmit}
                >
                  <Form.Group className="mb-3">
                    <Form.Label>Title :</Form.Label>
                    <Form.Control
                      name="title"
                      placeholder="Enter a title"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      isInvalid={formik.touched.title && !!formik.errors.title}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formik.errors.title}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="row gap-3">
                    <Form.Group className="col mb-3">
                      <Form.Label>Department :</Form.Label>
                      <Select
                        classNamePrefix="form-select"
                        name="department"
                        options={departments}
                        value={departments.find(
                          el => el.value === formik.values.department
                        )}
                        onChange={selected => {
                          formik.setFieldValue("department", selected?.value);
                          formik.setFieldValue("recipient", "");
                        }}
                      />
                      {formik.touched.department && formik.errors.department ? (
                        <span className="text-danger mt-2">
                          {formik.errors.department}
                        </span>
                      ) : null}
                    </Form.Group>

                    <Form.Group className="col mb-3">
                      <Form.Label>Recipient :</Form.Label>
                      <Select
                        classNamePrefix="form-select"
                        name="recipient"
                        options={users}
                        value={users.find(
                          el => el.value === formik.values.recipient
                        )}
                        onChange={selected =>
                          formik.setFieldValue("recipient", selected?.value)
                        }
                        isDisabled={isLoadingUsers}
                      />
                      {formik.touched.recipient && formik.errors.recipient ? (
                        <span className="text-danger mt-2">
                          {formik.errors.recipient}
                        </span>
                      ) : null}
                    </Form.Group>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Comment :</Form.Label>
                    <label className="border p-0 rounded pb-2 message-area d-block">
                      <Form.Control
                        name="comment"
                        value={formik.values.comment}
                        onChange={formik.handleChange}
                        as="textarea"
                        className="w-100 border-0 p-3"
                        placeholder="Reason for damage request"
                        rows={4}
                      />
                    </label>
                  </Form.Group>
                </Form>

                <div className="my-2" />

                <DamageRequestTable items={tableData} onRemove={removeRow} />

                <div className="d-flex justify-content-center m-4">
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setShowBatchPicker(true)}
                  >
                    + Add Item From Batch
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <span />
          <div className="proceed-actions">
            <Button
              disabled={createRequisitionMutation.isLoading}
              onClick={() => setShowNewRequisitionModal(false)}
              type="button"
              variant="white"
              className="border bg-white"
            >
              Cancel
            </Button>
            <Button
              disabled={createRequisitionMutation.isLoading}
              type="submit"
              form="damage-requisition-form"
              variant="primary"
            >
              {createRequisitionMutation.isLoading
                ? "Please wait..."
                : "Send request"}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <DamageBatchPicker
        show={showBatchPicker}
        onHide={() => setShowBatchPicker(false)}
        onAdd={handleBatchesAdded}
        existingBarcodes={tableData.map(el => el.batchBarCode)}
      />
    </>
  );
}
